/**
 * Canonical `lesson_type` tokens for **per–Can-Do** rows (lesson_no ≥ 1).
 *
 * Membership + default are defined in `lesson_can_do_types.json` (shared with Python
 * `app/core/lesson_can_do_contract.py` via the sync test in ai-tutor-service).
 *
 * Note: `LessonItem.lesson_type` from lesson index is **separate** metadata (TOC / RAG);
 * it is used only to pre-fill UI defaults, not as the persistence source of truth.
 */

import manifest from './lesson_can_do_types.json';

const _canonical = manifest.canonical_types.map((t) => String(t).trim().toLowerCase());
const _expected = [
  'story',
  'rhyme',
  'grammar',
  'reading',
  'vocabulary',
  'writing',
  'listening',
  'activity',
] as const;
if (_canonical.join('\0') !== _expected.join('\0')) {
  throw new Error(
    'lesson_can_do_types.json canonical_types must match built-in order/set (keep Python + TS in sync).'
  );
}
if (String(manifest.default_type).trim().toLowerCase() !== 'reading') {
  throw new Error('lesson_can_do_types.json default_type must be "reading" unless code is updated.');
}

export const LESSON_CAN_DO_TYPES = _expected;

export type LessonCanDoType = (typeof LESSON_CAN_DO_TYPES)[number];

export const DEFAULT_LESSON_CAN_DO_TYPE: LessonCanDoType = 'reading';

/** Human-readable labels for selects and chips. */
export const LESSON_CAN_DO_TYPE_LABELS: Record<LessonCanDoType, string> = {
  story: 'Story',
  rhyme: 'Rhyme',
  grammar: 'Grammar',
  reading: 'Reading',
  vocabulary: 'Vocabulary',
  writing: 'Writing',
  listening: 'Listening',
  activity: 'Activity',
};

export function isLessonCanDoType(s: string | null | undefined): s is LessonCanDoType {
  return !!s && (LESSON_CAN_DO_TYPES as readonly string[]).includes(s.trim().toLowerCase());
}

export function normalizeLessonCanDoType(s: string | null | undefined): string {
  return (s || '').trim().toLowerCase();
}

/**
 * Map lesson-index / RAG free-text labels to a canonical Can-Do lesson_type default.
 */
export function inferLessonCanDoTypeFromLessonIndex(raw: string | null | undefined): LessonCanDoType | null {
  if (!raw?.trim()) return null;
  const s = raw.toLowerCase();
  if (s.includes('vocab')) return 'vocabulary';
  if (s.includes('writ')) return 'writing';
  if (s.includes('listen')) return 'listening';
  if (s.includes('activ') || s.includes('exercise')) return 'activity';
  if (s.includes('rhyme')) return 'rhyme';
  if (s.includes('grammar')) return 'grammar';
  if (s.includes('read')) return 'reading';
  if (s.includes('story')) return 'story';
  return null;
}

/** Normalize `lesson_type` on outbound API payloads (lesson_no ≥ 1). */
export function normalizeCanDoLessonTypeField(lessonNo: number, lessonType: string | undefined): string | undefined {
  if (lessonNo < 1 || lessonType == null) return lessonType;
  const t = lessonType.trim().toLowerCase();
  return t || undefined;
}
